import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import WaveBackground from '../components/shared/WaveBackground';

import { STORAGE_KEYS } from '../constants/storage';
import { DASHBOARD_INSIGHTS, TAG_STYLES } from '../data/insights';

/**
 * Dashboard Page
 * 
 * Main dashboard interface for LAWNANCE with navigation pills, chat bar, and insights.
 * 
 * Exact styles from dashboard.css:
 * - Background: main gradient with increased wave intensity (opacity 0.8)
 * - Brand title: 4.5rem, 900 weight, letter-spacing 0.2em, gradient
 * - Container: max-width 1200px
 * - Padding: 80px 20px (body)
 * - Font: Inter
 */
const Dashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('chat');
  const [chatInput, setChatInput] = useState('');

  const handleTabClick = (tab) => {
    setActiveTab(tab.toLowerCase());
    navigate(`/app#${tab.toLowerCase()}`);
  };

  const handleChatSubmit = () => {
    if (chatInput.trim()) {
      // Save the message to localStorage so it can be continued in chat page
      const message = {
        id: Date.now(),
        role: 'user',
        content: chatInput.trim(),
        timestamp: new Date().toISOString()
      };
      localStorage.setItem(STORAGE_KEYS.DASHBOARD_PENDING_MESSAGE, JSON.stringify(message));
      
      // Clear the input
      setChatInput('');
      
      // Navigate to chat page
      navigate('/app#chat');
    }
  };

  const getTagClass = (tag) => {
    return TAG_STYLES[tag] || TAG_STYLES.tech;
  };

  return (
    <div 
      className="dashboard-page min-h-screen bg-main-gradient flex flex-col items-center pt-20 pb-12 md:py-16 lg:py-20 px-4 md:px-5 text-dashboard-primary-black overflow-hidden h-screen justify-start relative font-inter w-full" 
      style={{ 
        width: '100%',
        minWidth: '100%',
        maxWidth: '100%'
      }}
    >
      {/* Wave background - dashboard variant (increased intensity) */}
      <WaveBackground variant="dashboard" />

      {/* Overlay (transparent, for z-index layering) */}
      <div className="overlay absolute top-0 left-0 w-full h-full bg-transparent pointer-events-none z-1" />

      {/* Container */}
      <div className="container w-full max-w-dashboard-container relative mx-auto z-content px-4 md:px-6 flex flex-col items-center">
        {/* Header */}
        <header className="flex justify-between items-center mb-[5px] relative w-full z-content max-w-full">
          {/* Brand Title - MOBILE: Responsive scaling */}
          <h1
            className="brand-title text-5xl sm:text-6xl md:text-7xl lg:text-brand-title-dashboard font-black tracking-[0.2em] text-center w-full uppercase my-3 md:my-5 mb-4 md:mb-[25px] leading-none opacity-0 animate-fade-in-dashboard [animation-delay:0.5s] text-gradient-brand"
          >
            <span className="lawnance-brand">LAWNANCE</span>
          </h1>

          {/* User Profile (hidden initially in spec) */}
          {/* Can be added later if needed */}
        </header>

        {/* Navigation Pills */}
        <div className="nav-pill flex justify-center mt-0 mb-5 w-full max-w-full">
          <div className="pill-container glass-bg-dashboard-pill rounded-pill-container flex gap-1.5 opacity-0 animate-fade-in-dashboard [animation-delay:0.6s] border border-white/10 shadow-pill-container relative p-1.5 backdrop-blur-dashboard-pill"
          >
            {['Taxper', 'Chat', 'Contly'].map((tab) => (
              <button
                key={tab}
                onClick={() => handleTabClick(tab)}
                className={`pill-item py-3 md:py-3 px-5 md:px-7 rounded-[40px] cursor-pointer font-semibold transition-all duration-[250ms] ease-linear relative text-sm md:text-pill-item tracking-[0.3px] uppercase bg-transparent border-none outline-none active:scale-[0.98] min-h-[48px] ${
                  activeTab === tab.toLowerCase()
                    ? 'bg-white/12 text-white shadow-pill-item-active'
                    : 'text-white/70 hover:text-white/95 hover:scale-[1.03]'
                }`}
                style={
                  activeTab === tab.toLowerCase()
                    ? {
                        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3), 0 0 20px rgba(255, 255, 255, 0.1), 0 0 0 1px rgba(255, 255, 255, 0.2) inset',
                        textShadow: '0 0 15px rgba(255, 255, 255, 0.3)',
                      }
                    : {
                        textShadow: 'none',
                      }
                }
              >
                {tab}
              </button>
            ))}
          </div>
        </div>

        {/* Chat Bar - Relative positioning for both mobile and desktop */}
        <div className="search-container flex justify-center mb-5 w-full max-w-full relative">
          <div
            className="chat-bar w-full md:max-w-chat-bar mx-auto flex items-center gap-2 md:gap-3 py-1 pr-2 md:pr-3 pl-3 md:pl-5 rounded-chat-bar border border-white/10 glass-bg-chat-bar shadow-chat-bar opacity-0 animate-fade-in-dashboard [animation-delay:0.7s] transition-all duration-[250ms] ease-linear relative overflow-hidden hover:border-white/15 hover:bg-white/[0.08] hover:shadow-chat-bar-hover focus-within:border-white/20 focus-within:bg-white/[0.08] focus-within:shadow-chat-bar-focus focus-within:-translate-y-0.5 backdrop-blur-dashboard-chat-bar drop-shadow-glow-soft"
          >
            {/* Chat Icon */}
            <div
              className="chat-icon flex items-center justify-center w-8 h-8 md:w-9 md:h-9 rounded-full border border-white/30 text-white/70 flex-shrink-0 transition-all duration-[250ms] ease-linear shadow-chat-icon cursor-pointer outline-none hover:text-white/90 hover:scale-105 hover:shadow-chat-icon-hover active:scale-[0.98] bg-chat-icon-gradient backdrop-blur-dashboard-chat-icon"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
              </svg>
            </div>

            {/* Chat Input */}
            <input
              type="text"
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleChatSubmit()}
              placeholder="Ask anything about legal, tax, or contracts..."
              className="chat-input flex-1 py-3 md:py-[18px] px-2 md:px-3 border-none bg-transparent text-sm md:text-chat-input text-white/90 outline-none font-normal tracking-[0.3px] min-w-0 font-inter placeholder:text-white/50 placeholder:font-medium placeholder:text-sm md:placeholder:text-chat-input-placeholder focus-visible:outline-none"
            />

            {/* Send Button */}
            <button
              onClick={handleChatSubmit}
              className="chat-send-button w-9 h-9 md:w-11 md:h-11 rounded-full border border-white/35 text-white/70 cursor-pointer flex items-center justify-center transition-all duration-200 ease-linear flex-shrink-0 shadow-chat-send-button outline-none -ml-1 md:-ml-2 relative hover:border-white/45 hover:text-white/95 hover:scale-105 hover:shadow-chat-send-button-hover active:scale-95 active:shadow-chat-send-button-active focus-visible:outline-2 focus-visible:outline-white/50 focus-visible:outline-offset-2 bg-send-button-gradient backdrop-blur-dashboard-send-button"
            >
              {/* Inner highlight for physical button feel */}
              <span
                className="absolute top-[20%] left-[20%] w-[30%] h-[30%] rounded-full opacity-60 pointer-events-none"
                style={{
                  background: 'radial-gradient(circle, rgba(255, 255, 255, 0.3) 0%, transparent 70%)',
                }}
              />
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="relative z-1">
                <line x1="22" y1="2" x2="11" y2="13" />
                <polygon points="22 2 15 22 11 13 2 9 22 2" />
              </svg>
            </button>
          </div>
        </div>

        {/* Insights Section */}
        <h2 className="section-title text-xl md:text-2xl lg:text-section-title-dashboard mt-12 md:mt-16 lg:mt-[60px] mb-4 md:mb-[15px] font-normal text-white text-center opacity-0 animate-fade-in-dashboard [animation-delay:0.9s] w-full max-w-full">
          Latest Insights
        </h2>

        {/* Insights Wrapper with Scroll Animation */}
        <div
          className="insights-wrapper w-full overflow-hidden pb-5 mask-fade-x max-w-full"
        >
          <div className="insights-track flex gap-3 md:gap-[25px] w-max animate-scroll-right hover:[animation-play-state:paused]">
            {/* Duplicate insights for seamless scrolling */}
            {[...DASHBOARD_INSIGHTS, ...DASHBOARD_INSIGHTS].map((insight, index) => (
              <div
                key={`${insight.id}-${index}`}
                className="insight-card min-w-[240px] max-w-[280px] md:min-w-[300px] md:max-w-none bg-[rgba(255,255,255,0.95)] p-4 md:p-6 lg:p-[25px] rounded-lg md:rounded-insight-card shadow-insight-card flex flex-col items-start h-auto max-h-[140px] md:max-h-none opacity-0 animate-fade-in-dashboard"
                style={{
                  backdropFilter: 'blur(5px)',
                  animationDelay: `${1.1 + (index % 4) * 0.1}s`,
                }}
              >
                {/* Tag */}
                <span
                  className={`tag py-0.5 px-2 md:py-1.5 md:px-3 rounded-tag-dashboard text-[9px] md:text-tag font-bold uppercase mb-1.5 md:mb-[15px] ${getTagClass(
                    insight.tag
                  )}`}
                >
                  {insight.tag}
                </span>

                {/* Title */}
                <h4 className="text-xs md:text-lg lg:text-insight-card-title mb-1 md:mb-2.5 font-semibold line-clamp-2">
                  {insight.title}
                </h4>

                {/* Description */}
                <p className="text-[11px] md:text-base lg:text-insight-card-text text-dashboard-text-grey leading-[1.4] line-clamp-2">
                  {insight.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;

